"use client";

import { createContext, useContext, useEffect, type ReactNode } from "react";

import type {
  ExperimentAssignment,
  ExperimentAssignmentMap,
  FlagKey,
} from "../types";
import {
  EXPERIMENT_ASSIGNMENTS_COOKIE,
  FALLBACK_VARIATION,
} from "../constants";
import {
  parseAssignmentsCookie,
  serializeAssignmentsCookie,
} from "../utils/assignments-cookie";

const ExperimentContext = createContext<ExperimentAssignmentMap>({});

// Matches the gtm_id cookie's max-age so a persisted assignment never outlives
// the subject key it was resolved against. gtm_id is permanent, so the assignment
// never needs re-keying — see SLFSRV-3234.
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

// Persist the resolved assignments to a cookie the server can read on a later
// soft navigation, when a layout-mounted provider (and thus the server store)
// did not re-run. Merges into any existing cookie so multiple providers across
// pages accumulate rather than clobber. Skips forced variants (they live in the
// short-TTL `experimentVariant` cookie and must expire) and unresolved ones (so a
// transient API failure is never pinned for the cookie's lifetime).
function persistAssignments(value: ExperimentAssignmentMap): void {
  if (typeof document === "undefined") return;

  const existing = parseAssignmentsCookie(
    document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${EXPERIMENT_ASSIGNMENTS_COOKIE}=`))
      ?.slice(EXPERIMENT_ASSIGNMENTS_COOKIE.length + 1),
  );

  const merged = { ...existing };
  let changed = false;
  for (const [name, assignment] of Object.entries(value)) {
    if (!assignment || assignment.allocationId === "forced") continue;
    const { variation } = assignment;
    if (variation == null || variation === FALLBACK_VARIATION) continue;
    const variationStr = String(variation);
    if (merged[name] !== variationStr) {
      merged[name] = variationStr;
      changed = true;
    }
  }
  if (!changed) return;

  const attrs = [`path=/`, `max-age=${ONE_YEAR_SECONDS}`, "SameSite=Lax"];
  if (window.location.protocol === "https:") attrs.push("Secure");
  document.cookie = `${EXPERIMENT_ASSIGNMENTS_COOKIE}=${serializeAssignmentsCookie(merged)}; ${attrs.join("; ")}`;
}

export function ClientExperimentProvider({
  value,
  children,
}: {
  value: ExperimentAssignmentMap;
  children?: ReactNode;
}) {
  useEffect(() => {
    persistAssignments(value);
  }, [value]);

  return (
    <ExperimentContext.Provider value={value}>
      {children}
    </ExperimentContext.Provider>
  );
}

// Returns the assigned variant for a client component, or null when the
// experiment is unconfigured/unresolved so the caller renders the default.
export function useVariant(name: FlagKey): string | null {
  const assignment = useContext(ExperimentContext)[name];
  // Null check, not falsy: a BOOLEAN experiment can resolve to `false`, which
  // must stringify to "false" rather than be treated as unset.
  if (!assignment || assignment.variation == null) {
    return null;
  }
  return String(assignment.variation);
}

// Returns the full assignment entry (variation + allocationId) for a client
// component, or null when unconfigured/unresolved. ClientScopedExperiment uses
// this rather than useVariant so it can emit the allocationId debug attribute.
export function useExperimentAssignment(
  name: FlagKey,
): ExperimentAssignment | null {
  return useContext(ExperimentContext)[name] ?? null;
}

// The whole resolved assignment map for the page's provider, or an empty map
// with no ExperimentProvider ancestor. For consumers that must look up variants
// by name dynamically (they can't call useVariant per-name under the hooks
// rules) — e.g. content-flexibility keying a schema off an experiment.
export function useExperimentAssignments(): ExperimentAssignmentMap {
  return useContext(ExperimentContext);
}
